/**
 * Service worker registration and the install prompt.
 *
 * Chrome fires `beforeinstallprompt` once and expects the page to hold it until
 * the user asks — so it is captured here and replayed from a button, rather
 * than left to the browser's own banner, which is easy to miss on a page you
 * open daily.
 */

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  // Dev is served by Vite with no built assets to cache; registering there
  // would serve a stale shell over the hot-reloading one.
  if (!import.meta.env.PROD) return

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Offline support is an enhancement; failing to register must not break
      // the app, and there is nothing the user could do about it anyway.
    })
  })
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
const listeners = new Set<(available: boolean) => void>()

function announce() {
  for (const listener of listeners) listener(deferred !== null)
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as InstallPromptEvent
    announce()
  })

  window.addEventListener('appinstalled', () => {
    deferred = null
    announce()
  })
}

export function onInstallAvailable(listener: (available: boolean) => void): () => void {
  listeners.add(listener)
  listener(deferred !== null)
  return () => listeners.delete(listener)
}

/** True once the app is running from the home screen rather than a browser tab. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari predates display-mode and uses its own flag.
    (window.navigator as { standalone?: boolean }).standalone === true
  )
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable'
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  deferred = null
  announce()
  return outcome
}
